import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { TWeeklyStatisticsResponse } from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import { useModal } from '@hooks/modal/useModal';
import checkMarkButton from '@images/emojiis-ios/check-mark-button.svg';
import hammerAndWrench from '@images/emojiis-ios/hammer-and-wrench.svg';
import poo from '@images/emojiis-ios/poo.svg';
import type React from 'react';
import { useTranslation } from 'react-i18next';

export const WeeklyStatisticsModal: React.FC<TWeeklyStatisticsResponse> = ({
  goodAnswers,
  needsImprovementAnswers,
  irrelevantAnswers,
  notEvaluatedAnswers,
  alreadyEvaluatedAnswers,
  weekNumber,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  return (
    <form className='tailwind-modal-form flex-1 min-h-[200px] px-2'>
      <div className='tailwind-modal-body flex flex-col h-inherit border border-primary-base rounded-md bg-gray-100 px-0 py-0'>
        <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
          {t('train-ai.weekly-statistics.subtitle')} {weekNumber}
        </div>
        <div className='flex flex-col gap-2 p-3'>
          <ul className='space-y-1 text-sm text-gray-600'>
            <li className='flex items-center gap-1.5'>
              <img
                src={checkMarkButton}
                alt={t('train-ai.weekly-statistics.good-answers')}
                className='inline-flex w-4 h-4 items-center'
              />
              {t('train-ai.weekly-statistics.good-answers')}:{'  '}
              {goodAnswers + alreadyEvaluatedAnswers.good}
            </li>
            <li className='flex items-center gap-1.5'>
              <img
                src={hammerAndWrench}
                alt={t('train-ai.weekly-statistics.needs-improvement')}
                className='inline-flex w-4 h-4 items-center'
              />
              {t('train-ai.weekly-statistics.needs-improvement')}:{'  '}
              {needsImprovementAnswers +
                alreadyEvaluatedAnswers.needsImprovement}
            </li>
            <li className='flex items-center gap-1.5'>
              <img
                src={poo}
                alt={t('train-ai.weekly-statistics.irrelevant')}
                className='inline-flex w-4 h-4 items-center'
              />
              {t('train-ai.weekly-statistics.irrelevant')}:{'  '}
              {irrelevantAnswers + alreadyEvaluatedAnswers.irrelevant}
            </li>
            <li>
              <div className='tailwind-divider-x' />
            </li>
            <li>
              {t('train-ai.weekly-statistics.already-evaluated')}:{'  '}
              {alreadyEvaluatedAnswers.good +
                alreadyEvaluatedAnswers.needsImprovement +
                alreadyEvaluatedAnswers.irrelevant}
            </li>
            <li>
              {t('train-ai.weekly-statistics.not-evaluated')}:{'  '}
              {notEvaluatedAnswers}
            </li>
          </ul>
        </div>
        <div className='flex bg-primary-dark text-white text-sm font-normal py-1 px-3'>
          {t('train-ai.weekly-statistics.ai-accuracy')}
        </div>
        <div className='flex flex-col gap-2 p-3'>
          <ul className='space-y-2 text-sm text-gray-600'>
            <li>
              {t('train-ai.weekly-statistics.accuracy-good')}:{' '}
              {(
                ((goodAnswers + alreadyEvaluatedAnswers.good) /
                  Math.max(
                    1,
                    goodAnswers +
                      needsImprovementAnswers +
                      alreadyEvaluatedAnswers.good +
                      alreadyEvaluatedAnswers.needsImprovement,
                  )) *
                100
              ).toFixed(1)}
              %
              <br />
              <span className='text-pico italic text-gray-500'>
                {t('train-ai.weekly-statistics.accuracy-good-info')}
              </span>
            </li>
            <li>
              {t('train-ai.weekly-statistics.accuracy-needs-improvement')}:{' '}
              {(
                ((needsImprovementAnswers +
                  alreadyEvaluatedAnswers.needsImprovement) /
                  Math.max(
                    1,
                    goodAnswers +
                      needsImprovementAnswers +
                      alreadyEvaluatedAnswers.good +
                      alreadyEvaluatedAnswers.needsImprovement,
                  )) *
                100
              ).toFixed(1)}
              %
              <br />
              <span className='text-pico italic text-gray-500'>
                {t(
                  'train-ai.weekly-statistics.accuracy-needs-improvement-info',
                )}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div className='tailwind-modal-footer px-0'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
      </div>
    </form>
  );
};

export default WeeklyStatisticsModal;
